import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DownloadIcon,
  EyeOpenIcon,
  PaperPlaneIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Button,
  InvoiceDetailsModal,
  mapUserPlanToInvoiceDetails,
  Select,
  Spinner,
  Table,
  TableColumn,
} from '../../components';
import type { InvoiceDetails } from '../../components';
import { userPlanService } from '../../services/userPlanService';
import {
  formatInvoiceAmount,
  triggerBlobDownload,
  userInvoiceService,
} from '../../services/userInvoiceService';
import type {
  InvoiceFilters,
  InvoiceSummary,
  PlanStatusFilter,
  TimePeriodFilter,
} from '../../types/userInvoice';
import { UserPlan, UserPlansQueryParams } from '../../types/api';
import { formatDate, formatNumber, getFullName } from '../../utils/formatters';
import {
  buildInvoiceFilterQuery,
  DEFAULT_INVOICE_FILTERS,
  DURATION_MONTHS_OPTIONS,
  hasActiveInvoiceFilters,
  parsePlanStatusFilter,
  PLAN_STATUS_FILTERS,
  TIME_PERIOD_OPTIONS,
  toDurationMonthsFilter,
} from './invoiceFilters';
import { useAuthStore } from '../../store/authStore';
import { canDownloadInvoice, canSendInvoice } from '../../utils/rbac';
import './UserPlans.scss';

type FeedbackState = {
  type: 'success' | 'error';
  message: string;
} | null;

const EMPTY_SUMMARY: InvoiceSummary = {
  totalRevenue: 0,
  currency: 'INR',
  activePlans: 0,
  connectsUsed: {
    used: 0,
    total: 0,
    utilizationPercent: 0,
  },
};

const getInitials = (firstName: string, lastName: string): string => {
  const firstInitial = firstName?.trim()?.charAt(0) || '';
  const lastInitial = lastName?.trim()?.charAt(0) || '';
  return `${firstInitial}${lastInitial}`.toUpperCase() || 'UP';
};

const getPlanReference = (plan: UserPlan): string => {
  if (plan.user_plan_id != null) return String(plan.user_plan_id);
  return plan.unique_id || plan.planId || plan.user_id || '-';
};

const getUserPlanId = (plan: UserPlan): number | null => {
  if (plan.user_plan_id != null && Number.isFinite(Number(plan.user_plan_id))) {
    return Number(plan.user_plan_id);
  }

  const reference = getPlanReference(plan);
  const parsedReference = Number(reference);
  if (Number.isFinite(parsedReference) && parsedReference > 0) {
    return parsedReference;
  }

  return null;
};

const getActionErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  if (err instanceof Error && err.message.trim()) {
    return err.message;
  }

  return fallback;
};

const toUserPlansQuery = (
  filters: InvoiceFilters,
  overrides: Partial<UserPlansQueryParams> = {}
): UserPlansQueryParams => {
  const apiFilters = buildInvoiceFilterQuery(filters);

  return {
    ...overrides,
    ...apiFilters,
  };
};

export const UserPlans: React.FC = () => {
  const role = useAuthStore((state) => state.role);
  const allowDownload = canDownloadInvoice(role);
  const allowSend = canSendInvoice(role);
  const [searchParams] = useSearchParams();
  const [userPlans, setUserPlans] = useState<UserPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<InvoiceFilters>(() => ({
    ...DEFAULT_INVOICE_FILTERS,
    status: parsePlanStatusFilter(searchParams.get('status')),
  }));
  const [selectedPlan, setSelectedPlan] = useState<UserPlan | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetails | null>(null);
  const [summary, setSummary] = useState<InvoiceSummary>(EMPTY_SUMMARY);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [downloadingPlanId, setDownloadingPlanId] = useState<number | null>(null);
  const [sendingPlanId, setSendingPlanId] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const paginationRef = useRef(pagination);

  paginationRef.current = pagination;

  const fetchSummary = useCallback(async () => {
    setIsSummaryLoading(true);
    try {
      const response = await userInvoiceService.getSummary();
      setSummary(response);
    } catch (err: unknown) {
      console.error('Failed to fetch invoice summary:', err);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setIsSummaryLoading(false);
    }
  }, []);

  const fetchUserPlans = useCallback(async (params?: UserPlansQueryParams) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await userPlanService.getUserPlans({
        page: params?.page ?? paginationRef.current.page,
        limit: params?.limit ?? paginationRef.current.limit,
        search: params?.search,
        status: params?.status,
        timePeriod: params?.timePeriod,
        durationMonths: params?.durationMonths,
      });

      if (Array.isArray(response)) {
        setUserPlans(response);
        setPagination((prev) => ({ ...prev, total: response.length, totalPages: 1 }));
      } else {
        setUserPlans(response.data || []);
        if (response.metaData) {
          setPagination({
            page: response.metaData.page,
            limit: response.metaData.limit,
            total: response.metaData.total,
            totalPages: response.metaData.totalPages,
          });
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user plans';
      setError(errorMessage);
      setUserPlans([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserPlans(toUserPlansQuery(filters));
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 5000);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const applyFilters = useCallback((nextFilters: InvoiceFilters) => {
    setFilters(nextFilters);
    fetchUserPlans(toUserPlansQuery(nextFilters, { page: 1 }));
  }, [fetchUserPlans]);

  const handlePageChange = useCallback((page: number) => {
    fetchUserPlans(toUserPlansQuery(filters, { page }));
  }, [fetchUserPlans, filters]);

  const handleStatusChange = useCallback((status: PlanStatusFilter) => {
    applyFilters({ ...filters, status });
  }, [applyFilters, filters]);

  const handleTimePeriodChange = useCallback((value: string) => {
    applyFilters({
      ...filters,
      timePeriod: value as TimePeriodFilter,
    });
  }, [applyFilters, filters]);

  const handleDurationMonthsChange = useCallback((value: string) => {
    applyFilters({
      ...filters,
      durationMonths: toDurationMonthsFilter(value),
    });
  }, [applyFilters, filters]);

  const handleClearFilters = useCallback(() => {
    applyFilters(DEFAULT_INVOICE_FILTERS);
  }, [applyFilters]);

  const handleExportReport = useCallback(async () => {
    setIsExporting(true);
    try {
      await userInvoiceService.exportReport(buildInvoiceFilterQuery(filters));
      setFeedback({
        type: 'success',
        message: 'Invoice report exported successfully.',
      });
    } catch (err: unknown) {
      setFeedback({
        type: 'error',
        message: getActionErrorMessage(err, 'Failed to export invoice report.'),
      });
    } finally {
      setIsExporting(false);
    }
  }, [filters]);

  const openInvoiceDetails = useCallback((plan: UserPlan) => {
    setSelectedPlan(plan);
    setSelectedInvoice(mapUserPlanToInvoiceDetails(plan));
  }, []);

  const closeInvoiceDetails = useCallback(() => {
    setSelectedPlan(null);
    setSelectedInvoice(null);
  }, []);

  const handleDownloadPdf = useCallback(async (plan: UserPlan) => {
    const userPlanId = getUserPlanId(plan);
    if (!userPlanId) {
      setFeedback({
        type: 'error',
        message: 'Unable to download invoice: plan reference is missing.',
      });
      return;
    }

    setDownloadingPlanId(userPlanId);
    try {
      const { blob, filename } = await userInvoiceService.downloadPlanInvoice(userPlanId);
      triggerBlobDownload(blob, filename);
      setFeedback({
        type: 'success',
        message: 'Invoice PDF downloaded successfully.',
      });
    } catch (err: unknown) {
      setFeedback({
        type: 'error',
        message: getActionErrorMessage(err, 'Failed to download invoice PDF.'),
      });
    } finally {
      setDownloadingPlanId(null);
    }
  }, []);

  const handleSendInvoice = useCallback(async (plan: UserPlan) => {
    const userPlanId = getUserPlanId(plan);
    if (!userPlanId) {
      setFeedback({
        type: 'error',
        message: 'Unable to send invoice: plan reference is missing.',
      });
      return;
    }

    setSendingPlanId(userPlanId);
    try {
      await userInvoiceService.sendInvoice({ userPlanId });
      setFeedback({
        type: 'success',
        message: 'Invoice sent successfully.',
      });
    } catch (err: unknown) {
      setFeedback({
        type: 'error',
        message: getActionErrorMessage(err, 'Failed to send invoice.'),
      });
    } finally {
      setSendingPlanId(null);
    }
  }, []);

  const handleModalDownloadPdf = useCallback((_invoice: InvoiceDetails) => {
    if (selectedPlan) {
      void handleDownloadPdf(selectedPlan);
    }
  }, [handleDownloadPdf, selectedPlan]);

  const handleModalResendInvoice = useCallback((_invoice: InvoiceDetails) => {
    if (selectedPlan) {
      void handleSendInvoice(selectedPlan);
    }
  }, [handleSendInvoice, selectedPlan]);

  const hasActiveFilters = hasActiveInvoiceFilters(filters);

  const columns: TableColumn<UserPlan>[] = useMemo(() => [
    {
      key: 'planReference',
      header: 'Invoice ID',
      width: '140px',
      render: (item) => (
        <span className="table-cell-code">
          {getPlanReference(item)}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Client Name',
      render: (item) => (
        <div className="user-plans-client">
          <span className="user-plans-client__avatar">
            {getInitials(item.first_name, item.last_name)}
          </span>
          <div className="user-plans-client__copy">
            <span className="user-plans-client__name">
              {getFullName(item.first_name, null, item.last_name)}
            </span>
            <span className="user-plans-client__meta">{item.name || 'Subscription plan'}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'start_date',
      header: 'Date',
      width: '140px',
      render: (item) => (
        <span className="table-cell-date">{formatDate(item.start_date)}</span>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      width: '140px',
      render: (item) => (
        <span className="user-plans-amount">
          {formatInvoiceAmount(Number(item.total_price), item.currency ?? 'INR')}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Plan Status',
      width: '120px',
      render: (item) => (
        <Badge variant={item.is_active ? 'success' : 'warning'} size="small">
          {item.is_active ? 'Active' : 'Expired'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '130px',
      align: 'right',
      render: (item) => {
        const userPlanId = getUserPlanId(item);
        const isDownloading = userPlanId != null && downloadingPlanId === userPlanId;
        const isSending = userPlanId != null && sendingPlanId === userPlanId;

        return (
          <div className="user-plans-actions">
            <button
              type="button"
              className="user-plans-actions__button"
              onClick={(event) => {
                event.stopPropagation();
                openInvoiceDetails(item);
              }}
              aria-label={`View invoice for ${getFullName(item.first_name, null, item.last_name)}`}
            >
              <EyeOpenIcon />
            </button>
            {allowDownload && (
            <button
              type="button"
              className="user-plans-actions__button"
              disabled={!userPlanId || isDownloading}
              onClick={(event) => {
                event.stopPropagation();
                void handleDownloadPdf(item);
              }}
              aria-label={`Download invoice PDF for ${getFullName(item.first_name, null, item.last_name)}`}
            >
              {isDownloading ? <Spinner size="small" /> : <DownloadIcon />}
            </button>
            )}
            {allowSend && (
            <button
              type="button"
              className="user-plans-actions__button"
              disabled={!userPlanId || isSending}
              onClick={(event) => {
                event.stopPropagation();
                void handleSendInvoice(item);
              }}
              aria-label={`Send invoice for ${getFullName(item.first_name, null, item.last_name)}`}
            >
              <PaperPlaneIcon />
            </button>
            )}
          </div>
        );
      },
    },
  ], [
    allowDownload,
    allowSend,
    openInvoiceDetails,
    handleDownloadPdf,
    handleSendInvoice,
    downloadingPlanId,
    sendingPlanId,
  ]);

  const emptyMessage = filters.status !== 'all'
    ? `No ${filters.status} user plans found`
    : 'No user plans found';

  return (
    <div className="user-plans-page">
      <header className="user-plans-page__hero">
        <div>
          <h1>Invoice & Payment Management</h1>
          <p>Monitor revenue, track active plans, and manage user subscription usage.</p>
        </div>
        {allowDownload && (
        <Button
          type="button"
          className="user-plans-page__export"
          onClick={() => void handleExportReport()}
          disabled={isExporting}
        >
          {isExporting ? <Spinner size="small" /> : <DownloadIcon />}
          {isExporting ? 'Exporting…' : 'Export Report'}
        </Button>
        )}
      </header>

      <section className="user-plans-page__summary" aria-label="Invoice summary">
        <article className="user-plans-summary-card user-plans-summary-card--primary">
          <span className="user-plans-summary-card__label">Total Revenue</span>
          <strong className="user-plans-summary-card__value">
            {isSummaryLoading
              ? '—'
              : formatInvoiceAmount(summary.totalRevenue, summary.currency)}
          </strong>
        </article>
        <article className="user-plans-summary-card">
          <span className="user-plans-summary-card__label">Active Plans</span>
          <strong className="user-plans-summary-card__value">
            {isSummaryLoading ? '—' : formatNumber(summary.activePlans)}
          </strong>
        </article>
        <article className="user-plans-summary-card">
          <div className="user-plans-summary-card__copy">
            <span className="user-plans-summary-card__label">Connects Used</span>
            <span className="user-plans-summary-card__meta">
              {formatNumber(summary.connectsUsed.used)} of {formatNumber(summary.connectsUsed.total)} connects
            </span>
          </div>
          <strong className="user-plans-summary-card__value">
            {isSummaryLoading ? '—' : `${summary.connectsUsed.utilizationPercent}%`}
          </strong>
        </article>
      </section>

      <section className="user-plans-page__toolbar" aria-label="User plan filters">
        <div className="user-plans-page__toolbar-filters">
          <Select
            value={filters.timePeriod}
            onChange={handleTimePeriodChange}
            options={TIME_PERIOD_OPTIONS}
            searchable={false}
            fullWidth={false}
            className="user-plans-page__filter-select"
          />

          <div
            className="user-plans-page__filter-buttons"
            role="group"
            aria-label="Plan status filters"
          >
            {PLAN_STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                className={`user-plans-page__filter-btn ${filters.status === filter.value ? 'user-plans-page__filter-btn--active' : ''}`}
                aria-pressed={filters.status === filter.value}
                onClick={() => handleStatusChange(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <Select
            value={String(filters.durationMonths)}
            onChange={handleDurationMonthsChange}
            options={DURATION_MONTHS_OPTIONS}
            searchable={false}
            fullWidth={false}
            className="user-plans-page__filter-select user-plans-page__filter-select--duration"
          />
        </div>

        <button
          type="button"
          className="user-plans-page__filter-btn user-plans-page__filter-btn--clear"
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
        >
          Clear All
        </button>
      </section>

      {feedback && (
        <div
          className={`user-plans-page__feedback user-plans-page__feedback--${feedback.type}`}
          role="status"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      <Table
        columns={columns}
        data={userPlans}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        className="user-plans-table"
        onRowClick={openInvoiceDetails}
        pagination={{
          currentPage: pagination.page,
          totalPages: pagination.totalPages,
          pageSize: pagination.limit,
          total: pagination.total,
          onPageChange: handlePageChange,
        }}
      />

      <InvoiceDetailsModal
        isOpen={Boolean(selectedInvoice)}
        invoice={selectedInvoice}
        onClose={closeInvoiceDetails}
        onDownloadPdf={handleModalDownloadPdf}
        onResendInvoice={handleModalResendInvoice}
        isDownloading={
          selectedPlan != null
          && getUserPlanId(selectedPlan) != null
          && downloadingPlanId === getUserPlanId(selectedPlan)
        }
        isResending={
          selectedPlan != null
          && getUserPlanId(selectedPlan) != null
          && sendingPlanId === getUserPlanId(selectedPlan)
        }
      />
    </div>
  );
};
